import { useCallback, useEffect, useMemo, useRef } from 'react';

// One timeout you can re-arm and cancel, cleared when the component goes.
//
// ⭐ Why a primitive (LG-72 review, 2026-09-06). This is not an abstraction
// invented for a future caller: four sites had already hand-rolled the exact
// same six lines — a `useRef<ReturnType<typeof setTimeout> | null>`, a
// guard-clear-null helper, an arm that clears before it schedules, and an
// unmount effect that clears again. `useRestTimer`'s auto-hide, the transient
// tip's dismiss and the pain map's save debounce each carry their own copy,
// and LG-72's release grace was about to be the fourth. §CONSOLIDAMENTO's rule
// for that is not "collapse it later": the primitive is built inside the
// change that would have added the copy.
//
// ⛔ It owns the MECHANICS only. What to do on fire, and whether firing is
// still the right answer when it does, stays with the caller — the three
// existing sites disagree about that on purpose (one re-checks a Set, one
// resets a countdown, one persists a draft), and folding their decisions in
// here is how one primitive becomes three flags.
//
// ⚠️ `schedule` CLEARS a pending timer before arming the new one. That is the
// behaviour all four copies wrote by hand, and it is the one that matters: an
// event that re-arms must not leave the previous timer running underneath, or
// the callback fires on a schedule nobody asked for.

export type CancelableTimeout = {
  /** Arm the timer, cancelling any pending one first. */
  schedule: (fn: () => void, ms: number) => void;
  /** Cancel a pending timer. Safe to call when there is none. */
  cancel: () => void;
};

export function useCancelableTimeout(): CancelableTimeout {
  const handle = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (handle.current === null) return;
    clearTimeout(handle.current);
    handle.current = null;
  }, []);

  const schedule = useCallback(
    (fn: () => void, ms: number) => {
      cancel();
      handle.current = setTimeout(() => {
        // Cleared BEFORE the callback runs: a callback that re-arms (or that
        // throws) must not leave a stale handle behind claiming a timer is
        // still pending.
        handle.current = null;
        fn();
      }, ms);
    },
    [cancel],
  );

  // A pending timer must not outlive its owner: it would run against unmounted
  // state, and on a re-mount answer for something that no longer exists.
  useEffect(() => cancel, [cancel]);

  return useMemo(() => ({ schedule, cancel }), [schedule, cancel]);
}
